import React, { useState } from 'react';
import { supabase } from './supabaseClient.js';
import { computeTransitTravel } from './googleMapsTravel.js';
import CurrentLocationButton from './CurrentLocationButton.js';

const TransitFormModal = ({ routeId, dayNumber, sortOrder, priorEndpoint, existingItem, category = 'transit', onClose, onSaved }) => {
  const isEditing = !!existingItem;
  // Two estimate-layer kinds share this modal:
  //   'transit' — driving segment, origin→dest via Google Maps (computeTransitTravel)
  //   'other'   — non-driving (lunch, standby, repositioning): a duration + label, no Maps
  const effectiveCategory = existingItem?.category || category;
  const isOther = effectiveCategory === 'other';

  const [destination, setDestination] = useState(existingItem?.travel_destination || '');
  const [originOverride, setOriginOverride] = useState(existingItem?.travel_origin_override || '');
  const [hours, setHours] = useState(existingItem?.travel_hours ?? '');
  const [distanceKm, setDistanceKm] = useState(existingItem?.travel_distance_km ?? null);
  const [hoursLocked, setHoursLocked] = useState(existingItem?.travel_hours_locked || false);

  const [computing, setComputing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const effectiveOrigin = (originOverride.trim() || priorEndpoint || '').trim();

  const runCompute = async () => {
    const result = await computeTransitTravel({
      priorEndpoint: effectiveOrigin,
      transit: {
        travel_destination: destination.trim(),
        travel_origin_override: originOverride.trim() || null,
      },
    });
    if (result) {
      setHours(result.travel_hours);
      setDistanceKm(result.travel_distance_km);
    }
    return result;
  };

  const handleComputeClick = async () => {
    if (!destination.trim()) {
      setError('Enter a destination first.');
      return;
    }
    if (!effectiveOrigin) {
      setError('No prior endpoint and no origin override — enter an origin to compute travel.');
      return;
    }
    setError(null);
    setComputing(true);
    try {
      await runCompute();
    } catch (err) {
      setError(`Could not compute travel: ${err.message}`);
    } finally {
      setComputing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!destination.trim()) {
      setError(isOther ? 'Description is required.' : 'Destination is required.');
      return;
    }
    setError(null);
    setSaving(true);

    let finalHours = parseFloat(hours);
    let finalDistanceKm = distanceKm;
    let finalDescription;

    if (isOther) {
      // No Maps: a manually-entered duration plus a label. Lunch/standby/repositioning.
      if (isNaN(finalHours)) {
        setError('Hours required for an Other segment.');
        setSaving(false);
        return;
      }
      finalDistanceKm = null;
      finalDescription = destination.trim();
    } else {
      // Transit: auto-compute hours only when blank and not locked. Manual hours respected.
      const needsCompute = !hoursLocked && isNaN(finalHours);
      if (needsCompute) {
        if (!effectiveOrigin) {
          setError('No prior endpoint and no origin override — enter an origin, or enter hours manually.');
          setSaving(false);
          return;
        }
        try {
          const result = await runCompute();
          if (result) {
            finalHours = result.travel_hours;
            finalDistanceKm = result.travel_distance_km;
          }
        } catch (err) {
          setError(`Save failed: ${err.message}`);
          setSaving(false);
          return;
        }
      }

      if (isNaN(finalHours)) {
        setError('Hours required — enter a value or use Compute via Google Maps.');
        setSaving(false);
        return;
      }

      // Description is always derived from current origin/destination to stay in sync.
      finalDescription = `${(effectiveOrigin || 'Origin').split(',')[0].trim()} → ${destination.split(',')[0].trim()}`;
    }

    const payload = {
      row_type: 'TRANSIT',
      category: effectiveCategory,
      travel_destination: destination.trim(),
      travel_origin_override: isOther ? null : (originOverride.trim() || null),
      travel_hours: finalHours,
      travel_distance_km: isOther ? null : (hoursLocked ? null : finalDistanceKm),
      travel_description: finalDescription,
      travel_hours_locked: isOther ? false : hoursLocked,
      travel_type: null,
    };

    let saveError;
    if (isEditing) {
      const { error: err } = await supabase
        .from('route_items')
        .update(payload)
        .eq('id', existingItem.id);
      saveError = err;
    } else {
      const { error: err } = await supabase.from('route_items').insert([{
        ...payload,
        route_id: routeId,
        day_number: dayNumber,
        sort_order: sortOrder,
        crm_job_id: null,
      }]);
      saveError = err;
    }

    if (saveError) {
      console.error('Error saving Transit:', saveError);
      setError(`Save failed: ${saveError.message}`);
      setSaving(false);
    } else {
      onSaved();
    }
  };

  const inputClass =
    'w-full px-3 py-1.5 bg-gray-900 border border-gray-500 rounded-md text-white text-sm focus:outline-none focus:ring-2 focus:ring-green-600';
  const kindLabel = isOther ? 'Other Segment' : 'Travel';

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center px-4 pt-12 overflow-y-auto">
      <div className="fixed inset-0 bg-gray-900/80" onClick={onClose} />
      <div className="relative bg-gray-700 rounded-lg shadow-xl max-w-md w-full p-6 mb-12">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">
            {isEditing ? 'Edit' : 'Add'} {kindLabel} — Day {dayNumber}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-xl">&times;</button>
        </div>
        {error && (
          <div className="mb-4 px-3 py-2 bg-red-500/20 border border-red-500 rounded text-sm text-red-200">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-300 mb-1">
              {isOther ? 'Description *' : 'Destination *'}
            </label>
            <div className="relative flex items-center">
              {!isOther && (
                <span className="absolute left-2 z-10">
                  <CurrentLocationButton
                    onResolved={(addr) => { setDestination(addr); setDistanceKm(null); }}
                  />
                </span>
              )}
              <input
                type="text"
                value={destination}
                onChange={(e) => { setDestination(e.target.value); setDistanceKm(null); }}
                placeholder={isOther ? 'e.g. Lunch, standby, shop stop' : 'e.g. Kelowna, BC'}
                required
                className={`${inputClass} ${isOther ? '' : 'pl-9'}`}
              />
            </div>
          </div>

          {!isOther && (
            <div>
              <label className="block text-xs font-medium text-gray-300 mb-1">
                Origin (optional override)
              </label>
              <input
                type="text"
                value={originOverride}
                onChange={(e) => { setOriginOverride(e.target.value); setDistanceKm(null); }}
                placeholder={priorEndpoint || 'Will use prior endpoint'}
                className={inputClass}
              />
              {!originOverride && priorEndpoint && (
                <p className="text-[11px] text-gray-400 mt-1">
                  Defaults to prior endpoint: <span className="text-gray-300">{priorEndpoint}</span>
                </p>
              )}
              {!originOverride && !priorEndpoint && (
                <p className="text-[11px] text-amber-300 mt-1">
                  No prior endpoint detected — enter an origin to compute travel.
                </p>
              )}
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-xs font-medium text-gray-300">Hours{isOther ? ' *' : ''}</label>
              {!isOther && (
                <button
                  type="button"
                  onClick={handleComputeClick}
                  disabled={computing || !destination.trim() || !effectiveOrigin}
                  className="text-[11px] text-blue-300 hover:text-blue-300/80 disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Look up driving time via Google Maps"
                >
                  {computing ? 'Computing…' : 'Compute via Google Maps'}
                </button>
              )}
            </div>
            <input
              type="number"
              step="0.05"
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              placeholder="0.0"
              className={inputClass}
            />
            {!isOther && distanceKm != null && !hoursLocked && (
              <p className="text-[11px] text-gray-400 mt-1">{distanceKm} km (driving)</p>
            )}
          </div>

          {!isOther && (
            <div className="flex items-start gap-2 pt-1">
              <input
                type="checkbox"
                id="lock-hours"
                checked={hoursLocked}
                onChange={(e) => setHoursLocked(e.target.checked)}
                className="mt-0.5"
              />
              <label htmlFor="lock-hours" className="text-xs text-gray-300 leading-tight">
                Lock hours
                <span className="block text-[11px] text-gray-400">
                  Prevents Recalculate Travel from overwriting these hours. Use for plane, ferry, or any non-driving Transit.
                </span>
              </label>
            </div>
          )}

          <div className="flex items-center justify-end pt-3 border-t border-gray-500/30 gap-3">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-300 hover:text-white transition-colors">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-sm font-medium bg-green-700 text-white rounded-md hover:bg-green-600 disabled:opacity-50 transition-colors"
            >
              {saving ? 'Saving…' : isEditing ? 'Save Changes' : `Add ${kindLabel}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TransitFormModal;
